import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Car, ArrowLeft, Check, CreditCard, Shield, Clock, Users, Globe, MapPin, Loader2, Building2, AlertTriangle, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country flag mapping
const countryFlags = {
  FR: '🇫🇷',
  GB: '🇬🇧',
  ES: '🇪🇸',
  DE: '🇩🇪',
};

const Subscription = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(null);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // SEPA payment states
  const [sepaDialogOpen, setSepaDialogOpen] = useState(false);
  const [selectedPlanForSepa, setSelectedPlanForSepa] = useState(null);
  const [sepaForm, setSepaForm] = useState({
    iban: '',
    account_holder_name: '',
    email: user?.email || ''
  });
  const [sepaLoading, setSepaLoading] = useState(false);
  
  // 🚫 Pause commerciale — Mode liste d'attente pour le lancement du 05/10/2026
  // Fix 2026-09-21 : constante frontend qui garantit l'affichage de la bonne date
  // et du bon quota même si l'API backend prod renvoie encore l'ancienne valeur
  // (le backend prod peut retourner "2026-07-26" tant que la variable d'env
  // LAUNCH_DATE n'est pas mise à jour dans Emergent Deploy).
  const DISPLAY_LAUNCH_DATE = '2026-11-02';
  const LAUNCH_OFFER_START_MS = Date.parse('2026-11-01T23:00:00Z');
  const [publicConfig, setPublicConfig] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState(user?.email || '');
  const [waitlistJoined, setWaitlistJoined] = useState(false);

  useEffect(() => {
    // Reset redirecting state on mount
    setIsRedirecting(false);
    fetchPlans();
    fetchRegions();
    fetchUserSubscriptions();
    // Public config (subscription pause status, launch date)
    axios.get(`${API}/config/public`).then(r => setPublicConfig(r.data)).catch(() => {});
  }, []);

  // Ouverture automatique : recharge la config à l'heure d'ouverture si la page reste ouverte
  useEffect(() => {
    if (!publicConfig?.opens_at) return undefined;
    const delay = new Date(publicConfig.opens_at).getTime() - Date.now() + 2000;
    if (delay <= 0 || delay > 2147483000) return undefined;
    const timer = setTimeout(() => {
      axios.get(`${API}/config/public`).then(r => setPublicConfig(r.data)).catch(() => {});
    }, delay);
    return () => clearTimeout(timer);
  }, [publicConfig?.opens_at]);
  
  const joinWaitlist = async () => {
    if (!waitlistEmail || !waitlistEmail.includes('@')) {
      toast.error('Merci de renseigner un email valide');
      return;
    }
    try {
      const r = await axios.post(`${API}/waitlist/join`, {
        email: waitlistEmail,
        first_name: user?.first_name,
        region_id: selectedRegion?.id || 'paris'
      });
      toast.success(r.data.message);
      setWaitlistJoined(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur inscription liste prioritaire');
    }
  };
  
  useEffect(() => {
    if (user?.email) {
      setSepaForm(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  // Check URL for region parameter
  useEffect(() => {
    const regionParam = searchParams.get('region');
    if (regionParam && regions.length > 0) {
      const region = regions.find(r => r.id === regionParam);
      if (region) {
        setSelectedRegion(region);
      }
    }
  }, [searchParams, regions]);

  // Reload plans when region changes
  useEffect(() => {
    if (selectedRegion) {
      fetchPlans(selectedRegion.id);
    } else {
      fetchPlans();
    }
  }, [selectedRegion]);

  const fetchPlans = async (regionId = null) => {
    try {
      let url = `${API}/subscriptions/plans`;
      if (regionId) {
        url = `${API}/subscriptions/plans/region/${regionId}`;
      }
      const response = await axios.get(url);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API}/regions/active`);
      setRegions(response.data);
      // Auto-select first region if only one
      if (response.data.length === 1) {
        setSelectedRegion(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
    } finally {
      setRegionsLoading(false);
    }
  };

  const fetchUserSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/subscription/regions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
    }
  };

  const getSubscriptionForRegion = (regionId) => {
    return userSubscriptions.find(s => s.region_id === regionId && s.is_active);
  };

  const handleSubscribe = async (planId) => {
    // Empêcher les clics multiples
    if (loading || isRedirecting) {
      toast.warning(t('subscription.paymentInProgress', 'Paiement en cours, veuillez patienter...'));
      return;
    }
    
    if (!selectedRegion) {
      toast.error(t('regions.regionRequired', 'Please select a region first'));
      return;
    }

    setLoading(planId);
    setIsRedirecting(true); // Bloquer immédiatement
    
    try {
      const originUrl = window.location.origin;
      console.log('Creating checkout session...', { planId, regionId: selectedRegion.id });
      
      // Afficher un message de patience
      toast.info(t('subscription.creatingSession', 'Création de la session de paiement...'));
      
      const response = await axios.post(`${API}/payments/checkout/region`, {
        plan_id: planId,
        region_id: selectedRegion.id,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Checkout response:', response.data);
      
      if (response.data.url) {
        // Show toast before redirect
        toast.success(t('subscription.redirecting', 'Redirection vers Stripe...'));
        
        // Small delay to ensure state is updated before redirect
        setTimeout(() => {
          console.log('Redirecting to:', response.data.url);
          window.location.href = response.data.url;
        }, 300);
      } else {
        console.error('No URL in response:', response.data);
        toast.error(t('subscription.error', 'Error creating payment session'));
        setLoading(null);
        setIsRedirecting(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      console.error('Error response:', error.response?.data);
      const message = error.response?.data?.detail || t('subscription.error', 'Erreur lors de la création du paiement');
      toast.error(message);
      setLoading(null);
      setIsRedirecting(false);
    }
  };

  // Sogecommerce (Société Générale) Payment - auto-submit POST to vads-payment
  const handleSogecommerceSubscribe = async (planId) => {
    if (!selectedRegion) {
      toast.error(t('regions.regionRequired', 'Please select a region first'));
      return;
    }
    setLoading(planId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/sogecommerce/checkout`, {
        plan_id: planId,
        region_id: selectedRegion.id,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { action_url, fields } = response.data;
      if (!action_url || !fields) {
        toast.error('Erreur: payload Sogecommerce incomplet');
        setLoading(null);
        return;
      }

      setIsRedirecting(true);
      toast.success(t('subscription.redirectingSG', 'Redirection vers Société Générale...'));

      // Build hidden form and auto-submit (POST required by Sogecommerce)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = action_url;
      form.style.display = 'none';
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value == null ? '' : String(value);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      setTimeout(() => form.submit(), 200);
    } catch (error) {
      console.error('Sogecommerce checkout error:', error);
      const message = error.response?.data?.detail || 'Erreur Sogecommerce';
      toast.error(message);
      setLoading(null);
      setIsRedirecting(false);
    }
  };

  // SEPA Payment functions
  const openSepaDialog = (planId) => {
    if (!selectedRegion) {
      toast.error(t('regions.regionRequired', 'Please select a region first'));
      return;
    }
    setSelectedPlanForSepa(planId);
    setSepaDialogOpen(true);
  };

  const handleSepaPayment = async () => {
    if (!sepaForm.iban || !sepaForm.account_holder_name || !sepaForm.email) {
      toast.error(t('sepa.fillAllFields', 'Please fill all fields'));
      return;
    }

    // Basic IBAN validation (format check)
    const ibanClean = sepaForm.iban.replace(/\s/g, '').toUpperCase();
    if (ibanClean.length < 15 || ibanClean.length > 34) {
      toast.error(t('sepa.invalidIban', 'Invalid IBAN format'));
      return;
    }

    setSepaLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout/sepa`, {
        plan_id: selectedPlanForSepa,
        region_id: selectedRegion.id,
        iban: ibanClean,
        account_holder_name: sepaForm.account_holder_name,
        email: sepaForm.email,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'processing') {
        toast.success(t('sepa.processing', 'SEPA payment initiated! You will receive a confirmation email within 5-14 business days.'));
        setSepaDialogOpen(false);
        // Refresh subscriptions after a short delay
        setTimeout(() => {
          fetchUserSubscriptions();
        }, 2000);
      } else if (response.data.status === 'succeeded') {
        toast.success(t('sepa.success', 'SEPA payment successful!'));
        setSepaDialogOpen(false);
        fetchUserSubscriptions();
      } else if (response.data.status === 'requires_action') {
        toast.info(t('sepa.requiresAction', 'Additional action required'));
      } else {
        toast.info(response.data.message || 'Payment status: ' + response.data.status);
      }
    } catch (error) {
      console.error('SEPA error:', error);
      const message = error.response?.data?.detail || t('sepa.error', 'SEPA payment error');
      toast.error(message);
    } finally {
      setSepaLoading(false);
    }
  };

  // Show loading screen during redirect
  if (isRedirecting) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FFD60A] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-white text-xl font-semibold mb-2">{t('subscription.redirecting', 'Redirection vers le paiement...')}</p>
          <p className="text-zinc-400 text-sm">{t('subscription.pleaseWait', 'Veuillez patienter...')}</p>
        </div>
      </div>
    );
  }

  const planData = [
    { 
      id: '24h', 
      name: t('subscription.plans.day.name', '24 HEURES'), 
      price: t('subscription.plans.day.price', '6,99 €'), 
      period: t('subscription.plans.day.period', 'jour'),
      features: [
        t('subscription.plans.day.feature1', '5 trajets max sur 24h'), 
        t('subscription.plans.day.feature2', 'Accès à tous les véhicules'), 
        t('subscription.plans.day.feature3', 'Trajets optimisés')
      ]
    },
    { 
      id: '1week', 
      name: t('subscription.plans.week.name', '1 SEMAINE'), 
      price: t('subscription.plans.week.price', '19,99 €'), 
      period: t('subscription.plans.week.period', 'semaine'),
      badge: t('subscription.plans.week.badge', '-38% vs RATP'),
      features: [
        t('subscription.plans.week.feature1', '15 trajets sur 7 jours'),
        t('subscription.plans.week.feature2', 'Jusqu\'à 3 trajets/jour'),
        t('subscription.plans.week.feature3', 'Accès à tous les véhicules'),
        t('subscription.plans.week.feature4', '38% moins cher que le Navigo Semaine')
      ]
    },
    { 
      id: '1month', 
      name: t('subscription.plans.month.name', '1 MOIS'), 
      price: t('subscription.plans.month.price', '53,99 €'), 
      period: t('subscription.plans.month.period', 'mois'),
      popular: true,
      features: [
        t('subscription.plans.month.feature1', 'Trajets illimités pendant 30 jours'), 
        t('subscription.plans.month.feature2', 'Accès prioritaire'), 
        t('subscription.plans.month.feature3', 'Trajets optimisés'), 
        t('subscription.plans.month.feature4', 'Support prioritaire'), 
        t('subscription.plans.month.feature5', 'Économie maximale')
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/dashboard" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            {t('cgu.back', 'Retour')}
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <div className="flex items-center gap-2">
              <Car className="w-8 h-8 text-[#FFD60A]" />
              <span className="text-2xl font-black text-white">MÉTRO-TAXI</span>
            </div>
          </div>
        </div>

        {/* 🚫 PAUSE COMMERCIALE — Liste prioritaire de lancement (26/07/2026) */}
        {publicConfig?.subscriptions_paused && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-gradient-to-br from-[#FFD60A]/10 to-purple-500/10 border-2 border-[#FFD60A]/40 rounded-lg"
            data-testid="subscription-paused-banner"
          >
            <div className="flex items-start gap-4">
              <Clock className="w-8 h-8 text-[#FFD60A] flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-[#FFD60A] font-black text-xl mb-2">
                  Souscriptions en pause — Lancement officiel le {DISPLAY_LAUNCH_DATE}
                </h3>
                <p className="text-zinc-300 text-sm mb-4 leading-relaxed">
                  Métro-Taxi est en phase de finalisation technique. Pour garantir la qualité du service à nos premiers abonnés, <strong className="text-white">nous suspendons temporairement les nouvelles souscriptions</strong>.<br/>
                  <span className="text-zinc-400">Inscris-toi sur la liste prioritaire : à l’ouverture de la campagne Paris et petite couronne (codes postaux 75, 92, 93 et 94), les 30 premiers nouveaux abonnés éligibles recevront une première course réelle offerte jusqu’à 10 km routiers, après paiement de leur premier abonnement.</span>
                </p>
                
                {waitlistJoined ? (
                  <div className="flex items-center gap-2 text-green-400 font-medium">
                    <Check className="w-5 h-5" />
                    Tu es bien inscrit·e sur la liste prioritaire. À très bientôt !
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="email"
                      placeholder="ton.email@exemple.fr"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-white flex-1"
                      data-testid="waitlist-email-input"
                    />
                    <Button
                      onClick={joinWaitlist}
                      className="bg-[#FFD60A] hover:bg-[#E6C209] text-black font-bold whitespace-nowrap"
                      data-testid="waitlist-join-btn"
                    >
                      M&apos;inscrire en priorité
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {publicConfig && !publicConfig.subscriptions_paused && Date.now() < LAUNCH_OFFER_START_MS && (
          <div className="mb-8 p-4 bg-[#FFD60A]/5 border border-[#FFD60A]/40 rounded-lg text-sm text-zinc-300 leading-relaxed" data-testid="launch-offer-eligibility-notice">
            <strong className="text-[#FFD60A]">Abonnements ouverts.</strong> L’offre de lancement Paris et petite couronne (première course offerte jusqu’à 10 km routiers, 30 bénéficiaires maximum) concerne uniquement les premiers abonnements dont le paiement est confirmé entre le lundi 2 novembre 2026 à 00 h 00 et le lundi 16 novembre 2026 à 00 h 00 (heure de Paris). <strong className="text-white">Un abonnement payé avant le 2 novembre ne donne pas droit à la course offerte.</strong>
          </div>
        )}

        {/* PROTECTION DOUBLE PAIEMENT (C) - Message d'avertissement */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-amber-500/10 border border-amber-500/50 rounded-lg"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-amber-400 font-bold mb-1">
                  {t('subscription.alreadyAccount', 'Déjà inscrit ?')}
                </h4>
                <p className="text-zinc-300 text-sm mb-3">
                  {t('subscription.alreadyAccountDesc', 'Connectez-vous avec votre email pour retrouver votre abonnement sur tous vos appareils et éviter un double paiement.')}
                </p>
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-2 rounded transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  {t('auth.login.submit')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {user && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg"
          >
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-zinc-300 text-sm">
                <span className="text-blue-400 font-medium">{t('subscription.connectedAs', 'Connecté en tant que')} {user.email}</span>
                <br />
                {t('subscription.syncInfo', 'Votre abonnement sera automatiquement disponible sur tous vos appareils en vous connectant avec ce même email.')}
              </p>
            </div>
          </motion.div>
        )}

        {/* Current subscription status with renewal info */}
        {userSubscriptions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-green-500/10 border border-green-500/30 rounded"
            data-testid="current-subscription-card"
          >
            <h3 className="text-green-400 font-bold mb-3 flex items-center gap-2">
              <Check className="w-5 h-5" />
              {t('subscription.myCurrentSub', 'Mon abonnement actuel')}
            </h3>
            <div className="flex flex-wrap gap-3 mb-3">
              {userSubscriptions.filter(s => s.is_active).map(sub => {
                const hours = sub.hours_remaining || 0;
                const days = Math.floor(hours / 24);
                const remainingHours = hours % 24;
                const canRenewNow = hours <= 48;
                return (
                  <div key={sub.region_id} className="flex flex-col gap-2 bg-green-500/10 px-4 py-3 rounded-lg border border-green-500/30 w-full">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{countryFlags[sub.region?.country] || '🌍'}</span>
                      <span className="text-white font-bold">{sub.region?.name}</span>
                      <span className={`ml-auto text-xs px-2 py-1 rounded ${canRenewNow ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-green-500/20 text-green-300 border border-green-500/40'}`}>
                        {canRenewNow
                          ? t('subscription.renewSoon', 'À renouveler bientôt')
                          : t('subscription.statusActive', 'ACTIF')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-300 text-sm">
                      <Clock className="w-4 h-4 text-zinc-500" />
                      <span>
                        {days > 0
                          ? t('subscription.expiresInDays', { count: days, defaultValue: `Expire dans ${days} jour(s) ${remainingHours}h` })
                          : t('subscription.expiresInHours', { count: hours, defaultValue: `Expire dans ${hours}h` })}
                      </span>
                    </div>
                    {canRenewNow ? (
                      <p className="text-xs text-amber-300 mt-1">
                        💡 {t('subscription.renewAvailableNow', 'Tu peux renouveler maintenant pour éviter toute coupure de service. Sélectionne un forfait ci-dessous.')}
                      </p>
                    ) : (
                      <p className="text-xs text-zinc-500 mt-1">
                        🔒 {t('subscription.renewAvailableLater', `Le renouvellement sera disponible 48h avant l'expiration. Un email te sera envoyé.`)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Region Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-6 h-6 text-[#FFD60A]" />
            <h2 className="text-xl font-bold text-white">
              {t('regions.selectRegion', 'Select your region')}
            </h2>
          </div>
          
          {regionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#FFD60A]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {regions.map((region) => {
                const existingSub = getSubscriptionForRegion(region.id);
                return (
                  <button
                    key={region.id}
                    onClick={() => setSelectedRegion(region)}
                    className={`
                      relative p-4 rounded-xl border-2 transition-all text-left
                      ${selectedRegion?.id === region.id
                        ? 'border-[#FFD60A] bg-[#FFD60A]/10'
                        : 'border-zinc-700 hover:border-zinc-500 bg-zinc-800/50'
                      }
                    `}
                    data-testid={`subscription-region-${region.id}`}
                  >
                    {existingSub && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded">
                        {t('subscription.active', 'Actif')}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{countryFlags[region.country] || '🌍'}</span>
                      <div>
                        <h4 className="font-semibold text-white">{region.name}</h4>
                        <p className="text-sm text-zinc-400">
                          {region.currency}
                        </p>
                        {existingSub && (
                          <p className="text-xs text-green-400 mt-1">
                            {existingSub.hours_remaining}h {t('subscription.remaining', 'restantes')}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          
          {selectedRegion && (
            <p className="text-sm text-zinc-400 mt-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#FFD60A]" />
              {t('regions.subscriptionForRegion', 'Subscription for')}: <span className="text-white font-medium">{selectedRegion.name}</span>
            </p>
          )}
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            {t('subscription.chooseTitle', 'CHOISISSEZ VOTRE')} <span className="text-[#FFD60A]">{t('subscription.planWord', 'FORFAIT')}</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            {t('subscription.subtitle', 'Un abonnement simple. Des trajets illimités. Payez une fois, voyagez sans compter.')}
          </p>
        </motion.div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6">
          {planData.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`subscription-card ${plan.popular ? 'popular' : ''}`}
            >
              <h3 className="text-lg font-bold text-zinc-400 mb-4">{plan.name}</h3>
              
              <div className="mb-6">
                <span className="text-5xl font-black text-[#FFD60A]">{plan.price}</span>
              </div>
              
              <p className="text-zinc-500 mb-6">/ {plan.period}</p>
              
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-zinc-300">
                    <Check className="w-4 h-4 text-[#FFD60A] flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              {/* Stripe button (S'ABONNER doré) removed — Sogecommerce + SEPA only since 2026-06-03 */}

              {/* SEPA Button HIDDEN since 2026-06-06 — uses Stripe under the hood. Use Sogecommerce only. */}
              {false && selectedRegion?.currency === 'EUR' && (
                <Button
                  variant="outline"
                  onClick={() => openSepaDialog(plan.id)}
                  disabled={loading !== null || isRedirecting || sepaLoading || publicConfig?.subscriptions_paused}
                  className={`w-full h-10 mt-2 text-sm border-zinc-700 hover:bg-zinc-800 ${(loading !== null || isRedirecting || sepaLoading || publicConfig?.subscriptions_paused) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`sepa-${plan.id}-btn`}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  {t('sepa.payWithSepa', 'Payer par prélèvement SEPA')}
                </Button>
              )}

              {/* Sogecommerce (Société Générale) Button - Only for EUR regions */}
              {selectedRegion?.currency === 'EUR' && (
                <Button
                  variant="outline"
                  onClick={() => handleSogecommerceSubscribe(plan.id)}
                  disabled={loading !== null || isRedirecting || sepaLoading || publicConfig?.subscriptions_paused}
                  className={`w-full h-10 mt-2 text-sm border-red-700/40 text-red-200 hover:bg-red-950/40 ${(loading !== null || isRedirecting || sepaLoading || publicConfig?.subscriptions_paused) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`sogecommerce-${plan.id}-btn`}
                  title="Paiement sécurisé Société Générale (Apple Pay supporté)"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payer avec Société Générale
                </Button>
              )}
            </motion.div>
          ))}
        </div>

        {/* Payment methods */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-zinc-500 mb-4">{t('subscription.securePayments', 'Paiements sécurisés acceptés')}</p>
          <div className="flex justify-center flex-wrap gap-4">
            <div className="bg-zinc-800 px-4 py-2 rounded flex items-center gap-2">
              <span className="text-white text-sm font-medium">Visa</span>
            </div>
            <div className="bg-zinc-800 px-4 py-2 rounded flex items-center gap-2">
              <span className="text-white text-sm font-medium">Mastercard</span>
            </div>
            <div className="bg-zinc-800 px-4 py-2 rounded flex items-center gap-2">
              <span className="text-white text-sm font-medium">American Express</span>
            </div>
            <div className="bg-zinc-800 px-4 py-2 rounded flex items-center gap-2">
              <Building2 className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-medium">SEPA</span>
            </div>
          </div>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-12 grid md:grid-cols-3 gap-6"
        >
          <div className="flex items-center gap-4 p-4 bg-zinc-900/50 rounded border border-zinc-800">
            <Shield className="w-8 h-8 text-[#FFD60A]" />
            <div>
              <p className="text-white font-medium">Paiement sécurisé</p>
              <p className="text-zinc-500 text-sm">Via Société Générale</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-zinc-900/50 rounded border border-zinc-800">
            <Clock className="w-8 h-8 text-[#FFD60A]" />
            <div>
              <p className="text-white font-medium">Activation immédiate</p>
              <p className="text-zinc-500 text-sm">Voyagez tout de suite</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-zinc-900/50 rounded border border-zinc-800">
            <Users className="w-8 h-8 text-[#FFD60A]" />
            <div>
              <p className="text-white font-medium">Trajets illimités</p>
              <p className="text-zinc-500 text-sm">Sans surcoût</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* SEPA Payment Dialog */}
      <Dialog open={sepaDialogOpen} onOpenChange={setSepaDialogOpen}>
        <DialogContent className="bg-[#18181B] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-400" />
              {t('sepa.title', 'Prélèvement SEPA')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* SEPA Info */}
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
              <p className="text-zinc-300 text-sm">
                {t('sepa.sepaInfo', 'Le prélèvement SEPA est traité sous 5-14 jours ouvrés.')}
              </p>
            </div>

            {/* Plan info */}
            {selectedPlanForSepa && plans[selectedPlanForSepa] && (
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <p className="text-zinc-400 text-sm">{t('sepa.selectedPlan', 'Plan sélectionné')}</p>
                <p className="text-white font-bold text-lg">{plans[selectedPlanForSepa].name}</p>
                <p className="text-[#FFD60A] font-bold text-2xl">{plans[selectedPlanForSepa].price}€</p>
              </div>
            )}
            
            {/* IBAN Input */}
            <div className="space-y-2">
              <Label className="text-zinc-300">IBAN *</Label>
              <Input
                value={sepaForm.iban}
                onChange={(e) => setSepaForm(prev => ({ ...prev, iban: e.target.value.toUpperCase() }))}
                placeholder="FR76 1234 5678 9012 3456 7890 123"
                className="bg-zinc-900 border-zinc-700 text-white font-mono"
                data-testid="sepa-iban-input"
              />
              <p className="text-xs text-zinc-500">{t('sepa.ibanHelp', 'Votre IBAN bancaire (ex: FR76...)')}</p>
            </div>
            
            {/* Account Holder Name */}
            <div className="space-y-2">
              <Label className="text-zinc-300">{t('sepa.accountHolder', 'Titulaire du compte')} *</Label>
              <Input
                value={sepaForm.account_holder_name}
                onChange={(e) => setSepaForm(prev => ({ ...prev, account_holder_name: e.target.value }))}
                placeholder="Jean Dupont"
                className="bg-zinc-900 border-zinc-700 text-white"
                data-testid="sepa-name-input"
              />
            </div>
            
            {/* Email */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Email *</Label>
              <Input
                type="email"
                value={sepaForm.email}
                onChange={(e) => setSepaForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@exemple.com"
                className="bg-zinc-900 border-zinc-700 text-white"
                data-testid="sepa-email-input"
              />
            </div>
            
            {/* Mandate info */}
            <p className="text-xs text-zinc-500">
              {t('sepa.mandateInfo', 'En confirmant, vous autorisez Metro-Taxi à débiter votre compte via SEPA Direct Debit. Un mandat sera créé automatiquement.')}
            </p>
            
            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setSepaDialogOpen(false)}
                className="flex-1"
              >
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button
                onClick={handleSepaPayment}
                disabled={sepaLoading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-sepa-btn"
              >
                {sepaLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {t('sepa.confirm', 'Confirmer le prélèvement')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscription;
